import { useQueries } from '@tanstack/react-query'
import { useFormik } from 'formik'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap'
import * as Yup from 'yup'
import { getOfferMessage } from '../../../../api/offers/functions'
import offers from '../../../../api/offers/hooks'
import { MODAL_TYPES } from '../../../../common/constants'
import { offerModalFields } from '../../../../common/forms/generalRealEstate/fields'
import { offerSendInputs } from '../../../../common/forms/generalRealEstate/inputs'
import { hideModal } from '../../../../store/modal'
import RecursiveForm from '../../../RecursiveForm'
import { OfferSendModalProps } from '../types'

const OfferSendModal = (props: OfferSendModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const backfilledContactsRef = useRef<Set<string | number>>(new Set())

    const { isOpen, offerSendModal } = props
    const { items, agentId, offerType, offerEmailSettingsGroup, offerSettingsGroup, offerTemplate, offerLocale } = offerSendModal

    const { mutate: createOffer, isPending } = offers.CreateOffer.useMutation()

    const itemIds = useMemo(() => items?.map((item) => item.id) || [], [items])

    const messageQueries = useQueries({
        queries: (offerEmailSettingsGroup || []).map((contact) => ({
            queryKey: ['offerMessage', offerLocale, contact.contact_id, agentId, itemIds],
            queryFn: () =>
                getOfferMessage({
                    locale: offerLocale,
                    agentId: Number(agentId),
                    contactId: contact.contact_id,
                    ...(offerType === 'property' ? { propertyIds: itemIds } : { projectIds: itemIds }),
                }),
            enabled: !contact.offer_email_title || !contact.offer_email_message,
        })),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            offer_contact_select: offerEmailSettingsGroup?.[0]?.contact_id.toString() || '',
            emailDataGroup:
                offerEmailSettingsGroup?.map((contact) => ({
                    contact_id: contact.contact_id,
                    offer_email_title: contact.offer_email_title || '',
                    offer_email_message: contact.offer_email_message || '',
                })) || [],
        },
        validationSchema: Yup.object({
            offer_contact_select: Yup.string().required(),
            emailDataGroup: Yup.array().of(
                Yup.object({
                    contact_id: Yup.number().required(),
                    offer_email_title: Yup.string().required(t('modal.offerSend.emailTitle.error')),
                    offer_email_message: Yup.string().required(t('modal.offerSend.emailMessage.error')),
                })
            ),
        }),
        onSubmit: (values) => {
            const updatedEmailSettingsGroup = offerEmailSettingsGroup?.map((contact) => {
                const emailData = values.emailDataGroup.find((data) => data.contact_id === contact.contact_id)

                return {
                    ...contact,
                    offer_email_title: emailData?.offer_email_title,
                    offer_email_message: emailData?.offer_email_message,
                }
            })

            const createOfferPayload = {
                agent_id: agentId,
                offer_email_settings_group: updatedEmailSettingsGroup,
                offer_settings_group: offerSettingsGroup,
                offer_template: offerTemplate,
                offer_type: offerType,
                offer_locale: offerLocale,
            }

            // hideModal action is dispatched in hook onSuccess
            createOffer(createOfferPayload)
        },
    })

    const { values, setFieldValue } = formik

    // Backfills each contact's fetched default title/message once, without touching
    // other contacts' fields, so a slower query can't wipe out edits already made elsewhere in the form
    useEffect(() => {
        messageQueries.forEach((query, index) => {
            const contact = offerEmailSettingsGroup?.[index]

            if (!contact || !query.data || backfilledContactsRef.current.has(contact.contact_id)) {
                return
            }

            backfilledContactsRef.current.add(contact.contact_id)

            const currentValues = values.emailDataGroup[index]

            if (currentValues && !currentValues.offer_email_title) {
                setFieldValue(`emailDataGroup[${index}].offer_email_title`, query.data.title)
            }
            if (currentValues && !currentValues.offer_email_message) {
                setFieldValue(`emailDataGroup[${index}].offer_email_message`, query.data.message)
            }
        })
    }, [messageQueries, offerEmailSettingsGroup, values, setFieldValue])

    const selectOptions = useMemo(
        () => ({
            [offerModalFields.OFFER_CONTACT_SELECT]: offerEmailSettingsGroup?.map((item) => ({
                label: `${item.contact_name}, ${item.contact_email}`,
                value: item.contact_id.toString(),
            })),
        }),
        [offerEmailSettingsGroup]
    )

    const handleClose = () => dispatch(hideModal({ modalType: MODAL_TYPES.OFFER_SEND_MODAL }))

    return (
        <Modal isOpen={isOpen} centered>
            <ModalHeader toggle={handleClose}>{t('modal.offerSend.title')}</ModalHeader>
            <ModalBody className="p-5">
                <RecursiveForm
                    formik={formik}
                    inputs={offerSendInputs(formik.values.offer_contact_select, formik.values.emailDataGroup)}
                    selectOptions={selectOptions}
                    selectOptionsLoading={undefined}
                    subform={undefined}
                    config={undefined}
                    inputComponents={undefined}
                    fieldArrayDeleteComponent={undefined}
                />
                <div className="hstack gap-2 justify-content-end">
                    <Button type="submit" color="send" className="btn-label" onClick={() => formik.handleSubmit()}>
                        {isPending ? (
                            <i className="mdi mdi-spin mdi-loading label-icon align-middle fs-16 me-2"></i>
                        ) : (
                            <i className="mdi mdi-arrow-right align-bottom label-icon align-middle fs-16 me-2"></i>
                        )}
                        {t('button.send')}
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default OfferSendModal
